import { Platform } from "react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import { useTranslations } from "@src/packages/i18n";
import { useNotificationDeliverability } from "@src/packages/notifications";
import { useStytch } from "../../../../utils/useStytch";
import type { YouStackParamList } from "../../../types";
import { useYouScreen } from "../YouScreenContext";
import AccountFooter from "../sections/AccountFooter";
import LogoutModal from "../modals/LogoutModal";
import DeleteAccountModal from "../modals/DeleteAccountModal";
import Setup2FAModal from "../modals/Setup2FAModal";
import Disable2FAModal from "../modals/Disable2FAModal";
import YouDetailScreenLayout from "../components/YouDetailScreenLayout";
import { useSmartBack } from "../../../hooks/useSmartBack";
import { logoutWithRecovery } from "../../../../utils/logoutWithRecovery";

type Props = NativeStackScreenProps<YouStackParamList, "AccountSettings">;

export default function AccountSettingsScreen(_props: Props) {
  const handleBack = useSmartBack({
    fallbackTab: "Profile",
    fallbackScreen: "YouOverview",
  });
  const t = useTranslations();
  const stytch = useStytch();
  const { revokeRegisteredDevice } = useNotificationDeliverability();
  const { state, dispatch } = useYouScreen();

  return (
    <YouDetailScreenLayout
      title={t("you.panel_titles.account")}
      onBack={handleBack}
      overlay={
        <>
          <LogoutModal
            visible={state.activeModal === "logout"}
            onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            onConfirm={async () => {
              const sessionRevoked = await logoutWithRecovery({
                revokeRegisteredDevice,
                revokeSession: () =>
                  stytch.session.revoke({ forceClear: true }),
              });
              dispatch({ type: "SHOW_MODAL", modal: null });
              if (
                !sessionRevoked &&
                Platform.OS === "web" &&
                typeof window !== "undefined"
              ) {
                window.location.reload();
              }
            }}
          />

          <DeleteAccountModal
            visible={state.activeModal === "delete"}
            confirmText={state.deleteConfirmText}
            onChangeText={(text) =>
              dispatch({ type: "SET_DELETE_CONFIRM_TEXT", text })
            }
            onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
          />

          <Setup2FAModal
            visible={state.activeModal === "setup-2fa"}
            onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            onComplete={() => dispatch({ type: "SHOW_MODAL", modal: null })}
          />

          <Disable2FAModal
            visible={state.activeModal === "disable-2fa"}
            onClose={() => dispatch({ type: "SHOW_MODAL", modal: null })}
            onConfirm={() => dispatch({ type: "SHOW_MODAL", modal: null })}
          />
        </>
      }
    >
      <AccountFooter dispatch={dispatch} />
    </YouDetailScreenLayout>
  );
}
